import {
   MedicalServicePerformableByPractitionerSimple,
   ReceptionServiceForPractitionerSimple,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { commonTranslationKeys, createTranslatedText, useFormController } from '@dreryk/react-common';
import { Form, FormContent, Heading, Select } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { DiseaseAutosuggest } from '~libs.desktop.shared.visit-medical-documents.diagnoses';
import { ProcedureAutosuggest } from '~libs.desktop.shared.visit-medical-documents.procedures';

import { prescriptionVisitTemplatesTranslationKeys, visitTemplatesTranslationKeys } from '../translations';

import {
   PrescriptionVisitTemplatesFormBean,
   PrescriptionVisitTemplatesFormInitializer,
   PrescriptionVisitTemplatesFormRenderProps,
} from './PrescriptionVisitTemplatesForm.types';
import { StyledPrivateMedicalServicesSection } from './PrescriptionVisitTemplatesForm.styled';
import { PrescriptionVisitTemplatesTextEditorField } from './fields/PrescriptionVisitTemplatesTextEditorField';

const currentDate = new Date();

const nameTriggerLabelExtractor = (item?: { name?: string }): string => item?.name ?? '';

type PrescriptionVisitTemplatesFormProps = {
   readonly initializer: PrescriptionVisitTemplatesFormInitializer;
   readonly receptionServices: ReceptionServiceForPractitionerSimple[];
   readonly medicalServices: MedicalServicePerformableByPractitionerSimple[];
   readonly children: (renderProps: PrescriptionVisitTemplatesFormRenderProps) => JSX.Element;
};

// eslint-disable-next-line max-lines-per-function
export const PrescriptionVisitTemplatesForm = (props: PrescriptionVisitTemplatesFormProps): JSX.Element => {
   const initialBean = props.initializer();
   const { register, handleSubmit, formState, control } = useFormController<PrescriptionVisitTemplatesFormBean>(
      initialBean,
      { resetAfterSubmit: true }
   );

   return (
      <Form noValidate>
         <Heading
            type={'h2'}
            content={prescriptionVisitTemplatesTranslationKeys.sectionGeneral}
            testId={'sectionGeneralHeading'}
         />
         <FormContent testId={'prescriptionVisitTemplatesContent'}>
            <PrescriptionVisitTemplatesTextEditorField
               register={register}
               control={control}
               name={'interview'}
               label={visitTemplatesTranslationKeys.interview}
               placeholder={visitTemplatesTranslationKeys.enterInterview}
            />
            <PrescriptionVisitTemplatesTextEditorField
               register={register}
               control={control}
               name={'physicalExamination'}
               label={visitTemplatesTranslationKeys.subjectiveAssessment}
               placeholder={visitTemplatesTranslationKeys.enterSubjectiveAssessment}
            />
            <PrescriptionVisitTemplatesTextEditorField
               register={register}
               control={control}
               name={'recommendations'}
               label={visitTemplatesTranslationKeys.recommendations}
               placeholder={visitTemplatesTranslationKeys.enterRecommendations}
            />
            <DiseaseAutosuggest
               register={register}
               name={'diagnosis'}
               dictionaryValidOn={currentDate}
               defaultValue={initialBean.diagnosis}
               testId={'diagnosis'}
            />
            <ProcedureAutosuggest
               register={register}
               name={'procedure'}
               dictionaryValidOn={currentDate}
               defaultValue={initialBean.procedure}
               label={prescriptionVisitTemplatesTranslationKeys.procedure}
               testId={'procedure'}
            />
         </FormContent>

         {protectFeature(
            'performingPrivateMedicalServices',
            <StyledPrivateMedicalServicesSection>
               <Heading
                  type={'h2'}
                  content={prescriptionVisitTemplatesTranslationKeys.privateMedicalServices}
                  testId={'privateMedicalServicesHeading'}
               />
               <FormContent testId={'servicesSectionContent'}>
                  <Select<ReceptionServiceForPractitionerSimple>
                     triggerLabelExtractor={nameTriggerLabelExtractor}
                     controller={register('receptionService')}
                     label={prescriptionVisitTemplatesTranslationKeys.receptionService}
                     placeholder={commonTranslationKeys.choose}
                     testId={'receptionService'}
                     showClearIcon
                  >
                     <Select.List items={props.receptionServices} keyExtractor={'receptionServiceId'}>
                        {(item: ReceptionServiceForPractitionerSimple) => (
                           <Select.Item item={item} testId={item.name} label={createTranslatedText(item.name)} />
                        )}
                     </Select.List>
                  </Select>
                  <Select<MedicalServicePerformableByPractitionerSimple>
                     triggerLabelExtractor={nameTriggerLabelExtractor}
                     controller={register('medicalService')}
                     label={prescriptionVisitTemplatesTranslationKeys.medicalService}
                     placeholder={commonTranslationKeys.choose}
                     testId={'medicalService'}
                     showClearIcon
                  >
                     <Select.List items={props.medicalServices} keyExtractor={'medicalServiceId'}>
                        {(item: MedicalServicePerformableByPractitionerSimple) => (
                           <Select.Item item={item} testId={item.name} label={createTranslatedText(item.name)} />
                        )}
                     </Select.List>
                  </Select>
               </FormContent>
            </StyledPrivateMedicalServicesSection>,
            null
         )}

         {props.children({ handleSubmit: handleSubmit, formState: formState })}
      </Form>
   );
};
